// Keeping a board's document in step with storage.
//   CloudSync: server board over a WebSocket, with an IndexedDB copy for instant loads and offline edits.
//   LocalSync: a board that lives only in this browser (no account).
import type { Op, SepiaElement, ServerMessage } from "../../shared/model";
import { LIMITS, isPlausibleElement } from "../../shared/model";
import type { Editor } from "../editor/editor";
import type { Change } from "../editor/doc";
import type { SyncState } from "../board/TopBar";
import { api } from "./api";
import * as local from "./localdb";

export interface Presence {
  cid: string;
  name: string;
  x: number;
  y: number;
  tool: string;
  color: string;
  laser?: [number, number][];
  seen: number;
}

export interface BoardSync {
  readonly state: SyncState;
  subscribe(fn: () => void): () => void;
  start(): Promise<{ hadCache: boolean }>;
  destroy(): void;
  presence: Map<string, Presence>;
  subscribePresence(fn: () => void): () => void;
  sendPresence(p: { x: number; y: number; tool: string; color: string; laser?: [number, number][] }): void;
  /** resolves when every local edit has reached storage (or after `ms`) */
  flushNow(ms?: number): Promise<void>;
}

class Emitter {
  private ls = new Set<() => void>();
  on(fn: () => void) {
    this.ls.add(fn);
    return () => this.ls.delete(fn);
  }
  emit() {
    for (const l of this.ls) l();
  }
}

const CLIENT_ID = (() => {
  try {
    let id = sessionStorage.getItem("sepia.cid");
    if (!id) sessionStorage.setItem("sepia.cid", (id = crypto.randomUUID().slice(0, 12)));
    return id + "-" + Math.random().toString(36).slice(2, 6);
  } catch {
    return Math.random().toString(36).slice(2);
  }
})();

export class CloudSync implements BoardSync {
  state: SyncState = "connecting";
  presence = new Map<string, Presence>();
  private emitter = new Emitter();
  private presenceEmitter = new Emitter();
  private ws: WebSocket | null = null;
  private rev = 0;
  private pending = new Map<string, SepiaElement | null>();
  private inflight: { mid: number; ops: Op[] } | null = null;
  private mid = 0;
  private synced = false;
  private closed = false;
  private retry = 0;
  private retryTimer = 0;
  private flushTimer = 0;
  private cacheTimer = 0;
  private cacheDirty = new Map<string, SepiaElement | null>();
  private lastPresence = 0;
  private presenceTimer = 0;
  private pingTimer = 0;
  private offDoc: (() => void) | null = null;
  private cacheKey: string;
  private waiters: (() => void)[] = [];

  constructor(private ed: Editor, private boardId: string, private opts: { shareToken?: string; readOnly?: boolean } = {}) {
    this.cacheKey = opts.shareToken ? "s:" + opts.shareToken : "c:" + boardId;
  }

  subscribe(fn: () => void) {
    return this.emitter.on(fn);
  }
  subscribePresence(fn: () => void) {
    return this.presenceEmitter.on(fn);
  }

  private setState(s: SyncState) {
    if (this.state !== s) {
      this.state = s;
      this.emitter.emit();
    }
  }

  async start(): Promise<{ hadCache: boolean }> {
    const [cached, st] = await Promise.all([local.loadEls(this.cacheKey), local.getState(this.cacheKey)]);
    if (this.closed) return { hadCache: false };
    const hadCache = cached.length > 0 || (st?.rev ?? 0) > 0;
    if (hadCache) {
      this.ed.doc.reset(cached, "load");
      this.rev = st?.rev ?? 0;
      for (const op of st?.pending ?? []) this.pending.set(op.id, op.el);
      if (this.pending.size) {
        // edits made offline last time: show them and send them once connected
        const puts = [...this.pending.values()].filter(Boolean) as SepiaElement[];
        const dels = [...this.pending.entries()].filter(([, v]) => !v).map(([k]) => k);
        this.ed.doc.apply({ puts, dels }, "load");
      }
    } else {
      // no local copy: fetch a snapshot over HTTP so the board appears before the socket connects
      try {
        const snap = this.opts.shareToken ? await api.sharedSnapshot(this.opts.shareToken) : await api.snapshot(this.boardId);
        snap.els = snap.els.filter(isPlausibleElement);
        this.ed.doc.reset(snap.els, "load");
        this.rev = snap.rev;
        await local.replaceEls(this.cacheKey, snap.els);
        await this.saveState();
      } catch {
        /* offline and nothing cached: the socket will retry */
      }
    }
    if (this.closed) return { hadCache };
    this.offDoc = this.ed.doc.subscribe((c) => this.onLocalChange(c));
    window.addEventListener("online", this.onOnline);
    document.addEventListener("visibilitychange", this.onVisible);
    this.connect();
    return { hadCache };
  }

  destroy(): void {
    this.closed = true;
    this.offDoc?.();
    window.removeEventListener("online", this.onOnline);
    document.removeEventListener("visibilitychange", this.onVisible);
    window.clearTimeout(this.retryTimer);
    window.clearTimeout(this.flushTimer);
    window.clearInterval(this.pingTimer);
    this.writeCache();
    this.saveState();
    try {
      this.ws?.close(1000, "bye");
    } catch {
      /* ignore */
    }
    this.ws = null;
  }

  private onOnline = () => {
    if (!this.ws) {
      this.retry = 0;
      window.clearTimeout(this.retryTimer);
      this.connect();
    }
  };

  private onVisible = () => {
    if (document.visibilityState === "visible" && !this.ws && !this.closed) this.onOnline();
  };

  private wsUrl(): string {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const path = this.opts.shareToken ? `/api/shared/${this.opts.shareToken}/ws` : `/api/boards/${this.boardId}/ws`;
    return `${proto}//${location.host}${path}`;
  }

  private connect(): void {
    if (this.closed) return;
    this.setState(this.pending.size ? "offline" : "connecting");
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.wsUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      ws.send(JSON.stringify({ t: "hello", since: this.rev, cid: CLIENT_ID }));
      window.clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(`{"t":"ping"}`);
      }, 25000);
    };
    ws.onmessage = (e) => this.onMessage(typeof e.data === "string" ? e.data : "");
    ws.onclose = (e) => {
      if (this.ws === ws) this.ws = null;
      window.clearInterval(this.pingTimer);
      this.synced = false;
      if (this.inflight) {
        // unacknowledged ops go back into the queue (re-sending is harmless: last write wins)
        for (const op of this.inflight.ops) if (!this.pending.has(op.id)) this.pending.set(op.id, op.el);
        this.inflight = null;
      }
      if (e.code === 4004) {
        this.closed = true;
        this.setState("error");
        this.ed.host.toast("This board was deleted.", "error");
        return;
      }
      if (!this.closed) {
        this.setState(this.pending.size ? "offline" : "connecting");
        this.scheduleReconnect();
      }
    };
    ws.onerror = () => undefined;
  }

  private scheduleReconnect(): void {
    window.clearTimeout(this.retryTimer);
    const delay = [800, 1500, 3000, 6000, 12000, 20000][Math.min(this.retry++, 5)];
    this.retryTimer = window.setTimeout(() => this.connect(), delay + Math.random() * 400);
  }

  private async onMessage(raw: string): Promise<void> {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    switch (msg.t) {
      case "sync": {
        msg.els = msg.els.filter(isPlausibleElement);
        const skip = this.localIds();
        if (msg.full) {
          const keep = this.ed.doc.everything().filter((e) => skip.has(e.id));
          const incoming = msg.els.filter((e) => !skip.has(e.id));
          const ids = new Set([...incoming.map((e) => e.id), ...keep.map((e) => e.id)]);
          const dels = this.ed.doc.everything().filter((e) => !ids.has(e.id)).map((e) => e.id);
          this.ed.doc.apply({ puts: incoming, dels }, "remote");
          local.replaceEls(this.cacheKey, this.ed.doc.everything());
        } else {
          const puts = msg.els.filter((e) => !skip.has(e.id));
          const dels = msg.dels.filter((id) => !skip.has(id));
          this.ed.doc.apply({ puts, dels }, "remote");
          for (const e of puts) this.cacheDirty.set(e.id, e);
          for (const id of dels) this.cacheDirty.set(id, null);
          this.scheduleCache();
        }
        this.rev = Math.max(this.rev, msg.rev);
        this.synced = true;
        this.saveState();
        this.refreshState();
        this.flush();
        return;
      }
      case "resnapshot": {
        try {
          const snap = this.opts.shareToken ? await api.sharedSnapshot(this.opts.shareToken) : await api.snapshot(this.boardId);
          const skip = this.localIds();
          const incoming = snap.els.filter((e) => !skip.has(e.id) && isPlausibleElement(e));
          const ids = new Set([...incoming.map((e) => e.id), ...skip]);
          const dels = this.ed.doc.everything().filter((e) => !ids.has(e.id)).map((e) => e.id);
          this.ed.doc.apply({ puts: incoming, dels }, "remote");
          this.rev = snap.rev;
          await local.replaceEls(this.cacheKey, this.ed.doc.everything());
          this.saveState();
          this.ws?.send(JSON.stringify({ t: "hello", since: this.rev, cid: CLIENT_ID }));
        } catch {
          this.ws?.close();
        }
        return;
      }
      case "ack": {
        if (this.inflight && this.inflight.mid === msg.mid) this.inflight = null;
        this.rev = Math.max(this.rev, msg.rev);
        this.saveState();
        this.refreshState();
        this.flush();
        return;
      }
      case "ops": {
        this.rev = Math.max(this.rev, msg.rev);
        const skip = this.localIds();
        const puts: SepiaElement[] = [];
        const dels: string[] = [];
        for (const op of msg.ops) {
          if (skip.has(op.id) || (op.el && !isPlausibleElement(op.el))) continue;
          if (op.el) puts.push(op.el);
          else dels.push(op.id);
          this.cacheDirty.set(op.id, op.el);
        }
        this.ed.doc.apply({ puts, dels }, "remote");
        this.scheduleCache();
        this.saveStateSoon();
        return;
      }
      case "presence": {
        if (!Number.isFinite(msg.x) || !Number.isFinite(msg.y)) return;
        const laser = Array.isArray(msg.laser) ? msg.laser.filter((q) => Array.isArray(q) && Number.isFinite(q[0]) && Number.isFinite(q[1])) : undefined;
        this.presence.set(msg.cid, { ...msg, laser, name: String(msg.name ?? "").slice(0, 60), seen: Date.now() });
        this.presenceEmitter.emit();
        return;
      }
      case "left": {
        this.presence.delete(msg.cid);
        this.presenceEmitter.emit();
        return;
      }
      case "error": {
        this.ed.host.toast(msg.msg, "error");
        if (this.inflight) this.inflight = null;
        this.refreshState();
        return;
      }
    }
  }

  private localIds(): Set<string> {
    const s = new Set(this.pending.keys());
    if (this.inflight) for (const op of this.inflight.ops) s.add(op.id);
    return s;
  }

  private onLocalChange(c: Change): void {
    if (c.origin !== "local" && c.origin !== "history") return;
    if (this.opts.readOnly) return;
    for (const id of c.ids) {
      const el = this.ed.doc.get(id) ?? null;
      this.pending.set(id, el);
      this.cacheDirty.set(id, el);
    }
    this.scheduleCache();
    this.refreshState();
    window.clearTimeout(this.flushTimer);
    this.flushTimer = window.setTimeout(() => this.flush(), 40);
  }

  private flush(): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN || !this.synced || this.inflight || !this.pending.size) {
      if (!this.pending.size && !this.inflight) this.resolveWaiters();
      return;
    }
    const ops: Op[] = [];
    let bytes = 0;
    for (const [id, el] of this.pending) {
      const size = el ? JSON.stringify(el).length : id.length + 20;
      if (size > LIMITS.maxElementBytes) {
        this.pending.delete(id);
        this.ed.host.toast("One item is too large to save and was skipped.", "error");
        continue;
      }
      if (ops.length && (ops.length >= 300 || bytes + size > 700_000)) break;
      ops.push({ id, el });
      bytes += size;
    }
    for (const op of ops) this.pending.delete(op.id);
    if (!ops.length) return;
    this.inflight = { mid: ++this.mid, ops };
    ws.send(JSON.stringify({ t: "ops", mid: this.inflight.mid, ops }));
    this.refreshState();
    this.saveStateSoon();
  }

  private refreshState(): void {
    if (this.opts.readOnly) return this.setState("view");
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return this.setState(this.pending.size || this.inflight ? "offline" : "connecting");
    if (this.pending.size || this.inflight) this.setState("saving");
    else {
      this.setState("saved");
      this.resolveWaiters();
    }
  }

  private resolveWaiters(): void {
    const w = this.waiters;
    this.waiters = [];
    for (const f of w) f();
  }

  flushNow(ms = 2500): Promise<void> {
    if (!this.pending.size && !this.inflight) return Promise.resolve();
    this.flush();
    return new Promise((res) => {
      this.waiters.push(res);
      window.setTimeout(res, ms);
    });
  }

  private scheduleCache(): void {
    window.clearTimeout(this.cacheTimer);
    this.cacheTimer = window.setTimeout(() => this.writeCache(), 500);
  }

  private writeCache(): void {
    if (!this.cacheDirty.size) return;
    const puts: SepiaElement[] = [];
    const dels: string[] = [];
    for (const [id, el] of this.cacheDirty) {
      if (el) puts.push(el);
      else dels.push(id);
    }
    this.cacheDirty.clear();
    local.writeEls(this.cacheKey, puts, dels).catch(() => undefined);
  }

  private stateTimer = 0;
  private saveStateSoon(): void {
    window.clearTimeout(this.stateTimer);
    this.stateTimer = window.setTimeout(() => this.saveState(), 400);
  }

  private saveState(): Promise<void> {
    const pending: Op[] = [...this.pending.entries()].map(([id, el]) => ({ id, el }));
    if (this.inflight) for (const op of this.inflight.ops) if (!this.pending.has(op.id)) pending.push(op);
    return local.setState({ key: this.cacheKey, rev: this.rev, pending }).catch(() => undefined);
  }

  sendPresence(p: { x: number; y: number; tool: string; color: string; laser?: [number, number][] }): void {
    const now = performance.now();
    const send = () => {
      this.lastPresence = performance.now();
      if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: "presence", ...p }));
    };
    window.clearTimeout(this.presenceTimer);
    if (now - this.lastPresence > 70) send();
    else this.presenceTimer = window.setTimeout(send, 70);
  }
}

/** A board kept only in this browser. */
export class LocalSync implements BoardSync {
  state: SyncState = "local";
  presence = new Map<string, Presence>();
  private emitter = new Emitter();
  private offDoc: (() => void) | null = null;
  private dirty = new Map<string, SepiaElement | null>();
  private timer = 0;
  private closed = false;

  constructor(private ed: Editor, private boardId: string, private onSaved: (count: number) => void) {}

  subscribe(fn: () => void) {
    return this.emitter.on(fn);
  }
  subscribePresence(): () => void {
    return () => undefined;
  }
  sendPresence(): void {}

  async start(): Promise<{ hadCache: boolean }> {
    const els = await local.loadEls("g:" + this.boardId);
    if (this.closed) return { hadCache: false };
    this.ed.doc.reset(els, "load");
    this.offDoc = this.ed.doc.subscribe((c) => {
      if (c.origin !== "local" && c.origin !== "history") return;
      for (const id of c.ids) this.dirty.set(id, this.ed.doc.get(id) ?? null);
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.save(), 300);
    });
    return { hadCache: els.length > 0 };
  }

  private save(): Promise<void> {
    if (!this.dirty.size) return Promise.resolve();
    const puts: SepiaElement[] = [];
    const dels: string[] = [];
    for (const [id, el] of this.dirty) {
      if (el) puts.push(el);
      else dels.push(id);
    }
    this.dirty.clear();
    return local.writeEls("g:" + this.boardId, puts, dels).then(() => this.onSaved(this.ed.doc.size));
  }

  flushNow(): Promise<void> {
    window.clearTimeout(this.timer);
    return this.save();
  }

  destroy(): void {
    this.closed = true;
    this.offDoc?.();
    window.clearTimeout(this.timer);
    this.save();
  }
}
